import {
  gaqlerrArgsNumber as errArgsNumber,
  gaqlerrArgsNumberOneOf as errArgsNumberOneOf
} from '../gaqlerr.js'

import {
  gaqlutilQueryArgsOptGet as utilQueryArgsOptGet
} from '../gaqlutil.js'

// sketch
// const $now_spec = ['BGN', 0, 0] // BGN|END|CFG

// ```
// q.now() → time
// ```
const $now = (gst, qst) => {
  qst.target = new Date()

  return qst
}

const $toISO8601 = (gst, qst) => {
  const date = qst.target
  const tzo = -date.getTimezoneOffset()
  const dif = tzo >= 0 ? '+' : '-'
  const pad = num => (num < 10 ? '0' : '') + num

  qst.target = date.getFullYear() +
    '-' + pad(date.getMonth() + 1) +
    '-' + pad(date.getDate()) +
    'T' + pad(date.getHours()) +
    ':' + pad(date.getMinutes()) +
    ':' + pad(date.getSeconds()) +
    dif + pad(Math.floor(Math.abs(tzo) / 60)) +
    ':' + pad(Math.abs(tzo) % 60)

  return qst
}

const $toEpochTime = (gst, qst) => {
  qst.target = (new Date(qst.target)).getTime() / 1000

  return qst
}

const $date = (gst, qst) => {
  const date = new Date(qst.target)

  date.setMilliseconds(0)
  date.setSeconds(0)
  date.setMinutes(0)
  date.setHours(0)

  qst.target = date

  return qst
}

const $epochTime = (gst, qst, args) => {
  if (args.length !== 1)
    throw errArgsNumber('epochTime', 1, args.length, 1)

  qst.target = new Date(args[0] * 1000)

  return qst
}

// Create a time object based on an ISO 8601 date-time string
// (e.g. ‘2013-01-01T01:01:01+00:00’).
//
// ```
// q.ISO8601('1986-11-03T08:30:00-07:00')}).
// ```
const $ISO8601 = async (gst, qst, args, run) => {
  const isoargs = await run(gst, qst, args)
  const isoopts = utilQueryArgsOptGet(isoargs)

  if (isoargs.length > 2)
    throw errArgsNumber('ISO8601', 1, args.length, 2)
  if (typeof isoargs[0] !== 'string')
    throw errArgsNumber('ISO8601', 1, args.length, 2)

  const isostr = isoopts.defaultTimezone
    ? isoargs[0].replace(/-\d\d:\d\d$/, '') + isoopts.defaultTimezone
    : isoargs[0]

  qst.target = new Date(isostr)

  return qst
}


const $year = (gst, qst) => {
  qst.target = qst.target.getFullYear()

  return qst
}

const $month = (gst, qst) => {
  qst.target = qst.target.getMonth() + 1

  return qst
}

const $day = (gst, qst) => {
  qst.target = qst.target.getDate()

  return qst
}

const $dayOfYear = (gst, qst) => {
  // from koen peters
  const now = new Date(qst.target)
  const start = new Date(now.getFullYear(), 0, 0)
  const diff = now - start
  const oneDay = 1000 * 60 * 60 * 24
  const day = Math.floor(diff / oneDay)

  qst.target = day

  return qst
}

const $dayOfWeek = (gst, qst) => {
  qst.target = qst.target.getDay()

  return qst
}

// Return the hour in a time object as a number between 0 and 23.
const $hours = (gst, qst) => {
  qst.target = new Date(qst.target).getHours()

  return qst
}

const $minutes = (gst, qst) => {
  qst.target = new Date(qst.target).getMinutes()

  return qst
}

const $seconds = (gst, qst) => {
  qst.target = new Date(qst.target).getSeconds()

  return qst
}

const $toUnixTime = (gst, qst) => {
  qst.target = new Date(qst.target).getTime()

  return qst
}

// Create a time object for a specific time.
//
// A few restrictions exist on the arguments:
//  * year is an integer between 1400 and 9,999.
//  * month is an integer between 1 and 12.
//  * day is an integer between 1 and 31.
//  * hour is an integer.
//  * minutes is an integer.
//  * seconds is a double. Its value rounded to three decimal places
//    (millisecond-precision).
//  * timezone can be 'Z' (for UTC) or a string with the format ±[hh]:[mm].
//
// ```
// q.time(year, month, day[, hour, minute, second], timezone) → time
// ```
const $time = async (gst, qst, args, run) => {
  const timeargs = await run(gst, qst, args)

  if (timeargs.length < 4)
    throw errArgsNumber('time', 4, args.length, true)
  if (timeargs.length !== 4 && timeargs.length !== 7)
    throw errArgsNumberOneOf('time', [4, 7], timeargs.length)

  // Integer value representing the year.
  // Values from 0 to 99 map to the years 1900 to 1999.
  if (typeof timeargs[1] === 'number')
    timeargs[1] -= 1

  if (timeargs[6]) {
    console.log('[!!!] q.time: zerotimezone not supported')
    timeargs.splice(6, 1)
  }

  while (typeof timeargs.slice(-1)[0] !== 'number')
    timeargs.splice(timeargs.length -1, 1)

  qst.target = new Date(...timeargs)

  return qst
}

const $inTimezone = async (gst, qst, args, run) => {
  const timezone = (await run(gst, qst, args))[0] // ex, '-08:00'

  if (args.length !== 1)
    throw errArgsNumber('inTimezone', 1, args.length)

  const hhmm = timezone.split(':')
  const time = (+hhmm[0] * 60) + hhmm[1]
  qst.target = new Date(qst.target)
  qst.target.getTimezoneOffset = () => time

  // qst.target.timezoneOffset(time)
  // new Date().timezoneOffset(-180) // +3 UTC
  //  qst.target = new Date(qst.target.toISOString()
  //    .replace(/\.\d*Z$/, '') + timezone)

  return qst
}

// Return the timezone of the time object.
// ```
// time.timezone() → string
// ```
const $timezone = async (gst, qst) => {
  const timezoneMinutes = qst.target.getTimezoneOffset()

  // convert minutes to hours
  const timezoneHours = timezoneMinutes > 0
    ? Math.floor(timezoneMinutes / 60)
    : Math.ceil(timezoneMinutes / 60)
  const timezoneHoursMin = timezoneMinutes - timezoneHours * 60
  const timezonePre = timezoneMinutes > 0 ? '-' : ''

  const timezoneStr = timezonePre + [
    String('0' + timezoneHours).slice(-2),
    String('0' + timezoneHoursMin).slice(-2)
  ].join(':')

  qst.target = timezoneStr

  return qst
}

const $timeOfDay = (gst, qst) => {
  const day = qst.target

  qst.target = 0
    + day.getSeconds()
    + day.getMinutes() * 60
    + day.getHours() * 60 * 60

  return qst
}

// time.during(startTime, endTime[, {
//   leftBound: "closed", rightBound: "open"
// }]) → bool
const $during = async (gst, qst, args, run) => {
  const [start, end] = args
  const startTime = await run(gst, qst, start)
  const endTime = await run(gst, qst, end)

  if (args.length < 2)
    throw errArgsNumber('during', 2, args.length, true)

  if (args.length > 3)
    throw new Error(
      '`during` takes at most 3 arguments, ' + args.length + ' provided.')

  qst.target = (
    qst.target.getTime() > startTime.getTime()
      && qst.target.getTime() < endTime.getTime()
  )

  return qst
}

const $monday = 1
const $tuesday = 2
const $wednesday = 3
const $thursday = 4
const $friday = 5
const $saturday = 6
const $sunday = 7
const $january = 1
const $february = 2
const $march = 3
const $april = 4
const $may = 5
const $june = 6
const $july = 7
const $august = 8
const $september = 9
const $october = 10
const $november = 11
const $december = 12

export {
  $now,
  $toISO8601,
  $ISO8601,
  $toEpochTime,
  $date,
  $epochTime,
  $year,
  $month,
  $day,
  $dayOfYear,
  $dayOfWeek,
  $hours,
  $minutes,
  $seconds,
  $toUnixTime,
  $time,
  $inTimezone,
  $timezone,
  $timeOfDay,
  $during,

  $monday,
  $tuesday,
  $wednesday,
  $thursday,
  $friday,
  $saturday,
  $sunday,
  $january,
  $february,
  $march,
  $april,
  $may,
  $june,
  $july,
  $august,
  $september,
  $october,
  $november,
  $december
}
